// January 2027 roadmap calculators on the shared engine. Sources are named next to each
// constant; every formula is checked against a hand-worked value before shipping.

import type { FinanceField, FinanceMeta } from '@/lib/browser-finance-tools';
import type { CalcPack3Slug } from '@/lib/calc-pack-3-slugs';

const NOTE = 'Calculations run locally in your browser; nothing is uploaded.';
const HEALTH =
  'Calculations run locally in your browser. An estimate for general guidance, not medical advice.';
const BUILD = 'Estimates for planning; order 5-10% extra for waste and check local codes.';

const num = (key: string, label: string, def: string): FinanceField => ({
  key,
  label,
  placeholder: def,
  default: def,
});
const select = (
  key: string,
  label: string,
  def: string,
  options: Array<[string, string]>,
): FinanceField => ({
  key,
  label,
  placeholder: '',
  default: def,
  kind: 'select',
  options: options.map(([l, value]) => ({ label: l, value })),
});
const weight = (def = '70') => [
  num('w', 'Weight', def),
  select('wu', 'Weight in', '0', [
    ['kg', '0'],
    ['lb', '1'],
  ]),
];
const kgOf = (w: number, wu: number) => (wu === 1 ? w * 0.45359237 : w);

function fmt(v: number, digits = 2): string {
  if (!Number.isFinite(v)) return '—';
  return v.toLocaleString('en-US', { maximumFractionDigits: digits });
}

// Compendium of Physical Activities (2011) MET values.
const MET: Array<[string, number]> = [
  ['Walking, 3 mph (moderate)', 3.5],
  ['Walking, 4 mph (brisk)', 5.0],
  ['Hiking', 6.0],
  ['Running, 6 mph (10 min/mile)', 9.8],
  ['Running, 8 mph (7.5 min/mile)', 11.8],
  ['Cycling, 12-13.9 mph', 8.0],
  ['Swimming laps, moderate', 5.8],
  ['Rowing machine, moderate', 7.0],
  ['Elliptical, moderate', 5.0],
  ['Weight lifting, moderate', 3.5],
  ['Weight lifting, vigorous', 6.0],
  ['Circuit training / HIIT', 8.0],
  ['Jumping rope, moderate', 11.8],
  ['Aerobic dance', 7.3],
  ['Yoga (hatha)', 2.5],
  ['Tennis, singles', 8.0],
  ['Basketball, game', 8.0],
  ['Soccer, casual', 7.0],
];

// NEC Chapter 9, Table 8: DC resistance of uncoated stranded conductors, ohms per 1,000 ft at 75°C.
const AWG: Array<[string, number, number]> = [
  ['14 AWG', 3.07, 5.06],
  ['12 AWG', 1.93, 3.18],
  ['10 AWG', 1.21, 2.0],
  ['8 AWG', 0.764, 1.26],
  ['6 AWG', 0.491, 0.808],
  ['4 AWG', 0.308, 0.508],
  ['3 AWG', 0.245, 0.403],
  ['2 AWG', 0.194, 0.319],
  ['1 AWG', 0.154, 0.253],
  ['1/0 AWG', 0.122, 0.201],
  ['2/0 AWG', 0.0967, 0.159],
  ['3/0 AWG', 0.0766, 0.126],
  ['4/0 AWG', 0.0608, 0.1],
];

// Energy Star room air-conditioner sizing chart (square feet → BTU/h).
const BTU_CHART: Array<[number, number]> = [
  [150, 5000],
  [250, 6000],
  [300, 7000],
  [350, 8000],
  [400, 9000],
  [450, 10000],
  [550, 12000],
  [700, 14000],
  [1000, 18000],
  [1200, 21000],
  [1400, 23000],
  [1500, 24000],
  [2000, 30000],
  [2500, 34000],
];

function duration(sec: number): string {
  const t = Math.round(sec);
  const d = Math.floor(t / 86400);
  const h = Math.floor((t % 86400) / 3600);
  const m = Math.floor((t % 3600) / 60);
  const parts = [d ? `${d} d` : '', h ? `${h} h` : '', m ? `${m} min` : '', `${t % 60} s`];
  return parts.filter(Boolean).join(' ');
}

const volumeYd = (l: number, w: number, depthIn: number) => (l * w * (depthIn / 12)) / 27;
const areaFields = [
  num('l', 'Length (ft)', '20'),
  num('wd', 'Width (ft)', '10'),
  num('d', 'Depth (inches)', '3'),
];

export const calcPack3Meta: Record<CalcPack3Slug, FinanceMeta> = {
  'calories-burned-calculator': {
    key: 'calories-burned-calculator',
    title: 'Calories Burned Calculator',
    badge: 'MET',
    note: HEALTH,
    fields: [
      select(
        'act',
        'Activity',
        '0',
        MET.map(([name], i) => [name, String(i)]),
      ),
      num('min', 'Duration (minutes)', '30'),
      ...weight(),
    ],
    compute: ({ act, min, w, wu }) => {
      const [name, met] = MET[act] ?? MET[0];
      const kg = kgOf(w, wu);
      const kcal = (met * 3.5 * kg * min) / 200;
      return [
        { label: 'Calories burned', value: `${fmt(kcal, 0)} kcal` },
        { label: 'Per hour at this pace', value: `${fmt((kcal / min) * 60, 0)} kcal` },
        { label: 'Activity intensity', value: `${met} METs (${name})` },
      ];
    },
  },
  'bsa-calculator': {
    key: 'bsa-calculator',
    title: 'Body Surface Area Calculator',
    badge: 'BSA',
    note: HEALTH,
    fields: [num('h', 'Height (cm)', '175'), num('w', 'Weight (kg)', '70')],
    compute: ({ h, w }) => [
      { label: 'BSA (Mosteller)', value: `${fmt(Math.sqrt((h * w) / 3600), 3)} m²` },
      { label: 'BSA (DuBois)', value: `${fmt(0.007184 * w ** 0.425 * h ** 0.725, 3)} m²` },
      { label: 'BSA (Haycock)', value: `${fmt(0.024265 * w ** 0.5378 * h ** 0.3964, 3)} m²` },
    ],
  },
  'creatinine-clearance-calculator': {
    key: 'creatinine-clearance-calculator',
    title: 'Creatinine Clearance Calculator',
    badge: 'CrCl',
    note: HEALTH,
    fields: [
      num('age', 'Age (years)', '60'),
      num('w', 'Weight (kg)', '72'),
      num('scr', 'Serum creatinine (mg/dL)', '1.2'),
      select('sex', 'Sex', '0', [
        ['Male', '0'],
        ['Female', '1'],
      ]),
    ],
    compute: ({ age, w, scr, sex }) => {
      if (scr <= 0) return [{ label: 'CrCl', value: 'Enter serum creatinine' }];
      // Cockcroft-Gault (1976).
      const crcl = (((140 - age) * w) / (72 * scr)) * (sex === 1 ? 0.85 : 1);
      return [
        { label: 'Creatinine clearance (Cockcroft-Gault)', value: `${fmt(crcl, 1)} mL/min` },
        { label: 'Serum creatinine in µmol/L', value: fmt(scr * 88.42, 0) },
      ];
    },
  },
  'max-heart-rate-calculator': {
    key: 'max-heart-rate-calculator',
    title: 'Max Heart Rate Calculator',
    badge: 'HRmax',
    note: HEALTH,
    fields: [num('age', 'Age (years)', '35')],
    compute: ({ age }) => {
      const fox = 220 - age;
      const tanaka = 208 - 0.7 * age;
      return [
        { label: 'Max heart rate (Tanaka, 208 − 0.7 × age)', value: `${fmt(tanaka, 0)} bpm` },
        { label: 'Max heart rate (220 − age)', value: `${fmt(fox, 0)} bpm` },
        {
          label: 'Moderate zone (64-76%)',
          value: `${fmt(tanaka * 0.64, 0)}-${fmt(tanaka * 0.76, 0)} bpm`,
        },
        {
          label: 'Vigorous zone (77-93%)',
          value: `${fmt(tanaka * 0.77, 0)}-${fmt(tanaka * 0.93, 0)} bpm`,
        },
      ];
    },
  },
  'target-heart-rate-calculator': {
    key: 'target-heart-rate-calculator',
    title: 'Target Heart Rate Calculator',
    badge: 'Karvonen',
    note: HEALTH,
    fields: [
      num('age', 'Age (years)', '35'),
      num('rest', 'Resting heart rate (bpm)', '65'),
      num('pct', 'Target intensity (%)', '70'),
    ],
    compute: ({ age, rest, pct }) => {
      const max = 220 - age;
      const reserve = max - rest;
      const at = (p: number) => fmt(rest + reserve * p, 0);
      return [
        { label: `Target at ${fmt(pct, 0)}% (Karvonen)`, value: `${at(pct / 100)} bpm` },
        { label: 'Zone 1, 50-60% (recovery)', value: `${at(0.5)}-${at(0.6)} bpm` },
        { label: 'Zone 2, 60-70% (endurance)', value: `${at(0.6)}-${at(0.7)} bpm` },
        { label: 'Zone 3, 70-80% (tempo)', value: `${at(0.7)}-${at(0.8)} bpm` },
        { label: 'Zone 4, 80-90% (threshold)', value: `${at(0.8)}-${at(0.9)} bpm` },
        { label: 'Heart rate reserve', value: `${fmt(reserve, 0)} bpm (max ${fmt(max, 0)})` },
      ];
    },
  },
  'pregnancy-weight-gain-calculator': {
    key: 'pregnancy-weight-gain-calculator',
    title: 'Pregnancy Weight Gain Calculator',
    badge: 'IOM 2009',
    note: HEALTH,
    fields: [
      num('h', 'Height (cm)', '165'),
      num('w', 'Pre-pregnancy weight (kg)', '62'),
      select('twins', 'Pregnancy', '0', [
        ['Single baby', '0'],
        ['Twins', '1'],
      ]),
    ],
    compute: ({ h, w, twins }) => {
      const bmi = w / (h / 100) ** 2;
      const band = bmi < 18.5 ? 0 : bmi < 25 ? 1 : bmi < 30 ? 2 : 3;
      const single: Array<[number, number]> = [
        [28, 40],
        [25, 35],
        [15, 25],
        [11, 20],
      ];
      const twin: Array<[number, number] | null> = [null, [37, 54], [31, 50], [25, 42]];
      const range = twins === 1 ? twin[band] : single[band];
      const label = ['Underweight', 'Normal weight', 'Overweight', 'Obese'][band];
      return [
        { label: 'Pre-pregnancy BMI', value: `${fmt(bmi, 1)} (${label})` },
        {
          label: 'Recommended total gain (IOM 2009)',
          value: range
            ? `${range[0]}-${range[1]} lb (${fmt(range[0] * 0.4536, 1)}-${fmt(range[1] * 0.4536, 1)} kg)`
            : 'No IOM range for twins at this BMI; ask your provider',
        },
        ...(twins === 1
          ? []
          : [
              {
                label: 'Typical second- and third-trimester rate',
                value: [
                  '1 lb (0.45 kg) a week',
                  '1 lb (0.45 kg) a week',
                  '0.6 lb (0.28 kg) a week',
                  '0.5 lb (0.23 kg) a week',
                ][band],
              },
            ]),
      ];
    },
  },
  'waist-to-hip-ratio-calculator': {
    key: 'waist-to-hip-ratio-calculator',
    title: 'Waist to Hip Ratio Calculator',
    badge: 'WHR',
    note: HEALTH,
    fields: [
      num('waist', 'Waist', '84'),
      num('hip', 'Hips', '100'),
      select('sex', 'Sex', '0', [
        ['Male', '0'],
        ['Female', '1'],
      ]),
    ],
    compute: ({ waist, hip, sex }) => {
      if (!hip) return [{ label: 'Ratio', value: 'Enter both measurements' }];
      const r = waist / hip;
      const limit = sex === 1 ? 0.85 : 0.9;
      return [
        { label: 'Waist-to-hip ratio', value: fmt(r, 2) },
        {
          label: 'WHO assessment',
          value:
            r > limit
              ? `Above ${limit}: substantially increased metabolic risk`
              : `At or below ${limit}: lower risk`,
        },
      ];
    },
  },
  'army-body-fat-calculator': {
    key: 'army-body-fat-calculator',
    title: 'Army Body Fat Calculator',
    badge: 'Tape test',
    note: 'Calculations run locally. Uses the one-site formula in Army Directive 2023-11; your unit’s official measurement is what counts.',
    fields: [
      num('w', 'Weight (lb)', '180'),
      num('ab', 'Abdomen at the navel (inches)', '36'),
      select('sex', 'Sex', '0', [
        ['Male', '0'],
        ['Female', '1'],
      ]),
    ],
    compute: ({ w, ab, sex }) => {
      const bf = sex === 1 ? -9.15 - 0.015 * w + 1.27 * ab : -26.97 - 0.12 * w + 1.99 * ab;
      return [
        { label: 'Body fat (Army one-site tape)', value: `${fmt(bf, 1)}%` },
        { label: 'Fat mass', value: `${fmt((w * bf) / 100, 1)} lb` },
      ];
    },
  },
  'lean-body-mass-calculator': {
    key: 'lean-body-mass-calculator',
    title: 'Lean Body Mass Calculator',
    badge: 'LBM',
    note: HEALTH,
    fields: [
      num('h', 'Height (cm)', '178'),
      num('w', 'Weight (kg)', '80'),
      select('sex', 'Sex', '0', [
        ['Male', '0'],
        ['Female', '1'],
      ]),
    ],
    compute: ({ h, w, sex }) => {
      const boer = sex === 1 ? 0.252 * w + 0.473 * h - 48.3 : 0.407 * w + 0.267 * h - 19.2;
      const hume =
        sex === 1 ? 0.29569 * w + 0.41813 * h - 43.2933 : 0.3281 * w + 0.33929 * h - 29.5336;
      return [
        {
          label: 'Lean body mass (Boer)',
          value: `${fmt(boer, 1)} kg (${fmt((boer / w) * 100, 0)}%)`,
        },
        { label: 'Lean body mass (Hume)', value: `${fmt(hume, 1)} kg` },
        { label: 'Implied body fat (Boer)', value: `${fmt(100 - (boer / w) * 100, 1)}%` },
      ];
    },
  },
  'weight-loss-percentage-calculator': {
    key: 'weight-loss-percentage-calculator',
    title: 'Weight Loss Percentage Calculator',
    badge: '% lost',
    note: HEALTH,
    fields: [num('start', 'Starting weight', '95'), num('now', 'Current weight', '86')],
    compute: ({ start, now }) => {
      if (!start) return [{ label: 'Weight loss', value: 'Enter the starting weight' }];
      const lost = start - now;
      return [
        { label: 'Weight lost', value: `${fmt((lost / start) * 100, 2)}% of starting weight` },
        { label: 'Amount lost', value: fmt(lost, 1) },
        {
          label: 'Clinically meaningful (5%)?',
          value:
            lost / start >= 0.05
              ? 'Yes, 5% or more'
              : `Not yet: ${fmt(start * 0.05 - lost, 1)} to go`,
        },
      ];
    },
  },
  'adjusted-body-weight-calculator': {
    key: 'adjusted-body-weight-calculator',
    title: 'Adjusted Body Weight Calculator',
    badge: 'AdjBW',
    note: HEALTH,
    fields: [
      num('h', 'Height (inches)', '68'),
      num('w', 'Actual weight (kg)', '110'),
      select('sex', 'Sex', '0', [
        ['Male', '0'],
        ['Female', '1'],
      ]),
    ],
    compute: ({ h, w, sex }) => {
      // Devine ideal body weight; adjusted = IBW + 0.4 × (actual − IBW).
      const ibw = (sex === 1 ? 45.5 : 50) + 2.3 * (h - 60);
      const adj = ibw + 0.4 * (w - ibw);
      return [
        { label: 'Adjusted body weight', value: `${fmt(adj, 1)} kg` },
        { label: 'Ideal body weight (Devine)', value: `${fmt(ibw, 1)} kg` },
        { label: 'Actual weight as % of ideal', value: `${fmt((w / ibw) * 100, 0)}%` },
      ];
    },
  },
  'steps-to-calories-calculator': {
    key: 'steps-to-calories-calculator',
    title: 'Steps to Calories Calculator',
    badge: 'Steps',
    note: HEALTH,
    fields: [num('steps', 'Steps', '10000'), num('h', 'Height (cm)', '175'), ...weight()],
    compute: ({ steps, h, w, wu }) => {
      const kg = kgOf(w, wu);
      const km = (steps * h * 0.414) / 100 / 1000;
      const hours = km / 4.828; // 3 mph
      const kcal = 3.5 * kg * hours; // MET 3.5
      return [
        { label: 'Calories burned walking', value: `${fmt(kcal, 0)} kcal` },
        { label: 'Distance', value: `${fmt(km, 2)} km (${fmt(km / 1.609344, 2)} mi)` },
        {
          label: 'Walking time at 3 mph',
          value: `${Math.floor(hours)} h ${Math.round((hours % 1) * 60)} min`,
        },
      ];
    },
  },
  'molarity-calculator': {
    key: 'molarity-calculator',
    title: 'Molarity Calculator',
    badge: 'mol/L',
    note: NOTE,
    fields: [
      select('solve', 'Solve for', '0', [
        ['Molarity (from mass and volume)', '0'],
        ['Mass needed (from target molarity)', '1'],
      ]),
      num('mw', 'Molar mass (g/mol)', '58.44'),
      num('v', 'Solution volume (mL)', '500'),
      num('m', 'Mass of solute (g)', '14.61'),
      num('c', 'Target molarity (mol/L)', '0.5'),
    ],
    compute: ({ solve, mw, v, m, c }) => {
      const L = v / 1000;
      if (!mw || !L) return [{ label: 'Result', value: 'Enter the molar mass and volume' }];
      if (solve === 1)
        return [
          { label: 'Mass to dissolve', value: `${fmt(c * mw * L, 4)} g` },
          { label: 'Moles', value: fmt(c * L, 5) },
        ];
      return [
        { label: 'Molarity', value: `${fmt(m / mw / L, 4)} M (mol/L)` },
        { label: 'Moles of solute', value: fmt(m / mw, 5) },
        { label: 'Millimolar', value: `${fmt((m / mw / L) * 1000, 2)} mM` },
      ];
    },
  },
  'dilution-calculator': {
    key: 'dilution-calculator',
    title: 'Dilution Calculator',
    badge: 'C₁V₁ = C₂V₂',
    note: NOTE,
    fields: [
      num('c1', 'Stock concentration C₁', '10'),
      num('c2', 'Final concentration C₂', '2'),
      num('v2', 'Final volume V₂', '100'),
    ],
    compute: ({ c1, c2, v2 }) => {
      if (!c1) return [{ label: 'V₁', value: 'Enter the stock concentration' }];
      if (c2 > c1)
        return [{ label: 'V₁', value: 'The final concentration cannot exceed the stock' }];
      const v1 = (c2 * v2) / c1;
      return [
        { label: 'Stock volume V₁', value: fmt(v1, 4) },
        { label: 'Diluent to add', value: fmt(v2 - v1, 4) },
        { label: 'Dilution factor', value: `1:${fmt(c1 / c2, 3)}` },
      ];
    },
  },
  'density-calculator': {
    key: 'density-calculator',
    title: 'Density Calculator',
    badge: 'ρ = m/V',
    note: NOTE,
    fields: [
      select('solve', 'Solve for', '0', [
        ['Density', '0'],
        ['Mass', '1'],
        ['Volume', '2'],
      ]),
      num('m', 'Mass (g)', '250'),
      num('v', 'Volume (cm³ / mL)', '100'),
      num('d', 'Density (g/cm³)', '2.5'),
    ],
    compute: ({ solve, m, v, d }) => {
      if (solve === 1) return [{ label: 'Mass', value: `${fmt(d * v, 4)} g` }];
      if (solve === 2)
        return [{ label: 'Volume', value: d ? `${fmt(m / d, 4)} cm³` : 'Density cannot be 0' }];
      if (!v) return [{ label: 'Density', value: 'Volume cannot be 0' }];
      const rho = m / v;
      return [
        { label: 'Density', value: `${fmt(rho, 4)} g/cm³` },
        { label: 'In kg/m³', value: fmt(rho * 1000, 1) },
        {
          label: 'Floats in water?',
          value: rho < 1 ? 'Yes (less dense than water)' : rho > 1 ? 'No, it sinks' : 'Neutral',
        },
      ];
    },
  },
  'heat-index-calculator': {
    key: 'heat-index-calculator',
    title: 'Heat Index Calculator',
    badge: 'Feels like',
    note: NOTE,
    fields: [
      num('t', 'Air temperature', '92'),
      select('tu', 'Unit', '0', [
        ['°F', '0'],
        ['°C', '1'],
      ]),
      num('rh', 'Relative humidity (%)', '60'),
    ],
    compute: ({ t, tu, rh }) => {
      const T = tu === 1 ? (t * 9) / 5 + 32 : t;
      // NWS: simple formula first; Rothfusz regression with adjustments when the result is 80°F+.
      let hi = 0.5 * (T + 61 + (T - 68) * 1.2 + rh * 0.094);
      if ((hi + T) / 2 >= 80) {
        hi =
          -42.379 +
          2.04901523 * T +
          10.14333127 * rh -
          0.22475541 * T * rh -
          0.00683783 * T * T -
          0.05481717 * rh * rh +
          0.00122874 * T * T * rh +
          0.00085282 * T * rh * rh -
          0.00000199 * T * T * rh * rh;
        if (rh < 13 && T >= 80 && T <= 112)
          hi -= ((13 - rh) / 4) * Math.sqrt((17 - Math.abs(T - 95)) / 17);
        if (rh > 85 && T >= 80 && T <= 87) hi += ((rh - 85) / 10) * ((87 - T) / 5);
      }
      const level =
        hi >= 125
          ? 'Extreme danger'
          : hi >= 103
            ? 'Danger'
            : hi >= 90
              ? 'Extreme caution'
              : hi >= 80
                ? 'Caution'
                : 'No heat warning level';
      return [
        { label: 'Heat index', value: `${fmt(hi, 0)}°F (${fmt(((hi - 32) * 5) / 9, 0)}°C)` },
        { label: 'NWS level', value: level },
      ];
    },
  },
  'btu-calculator': {
    key: 'btu-calculator',
    title: 'BTU Calculator',
    badge: 'BTU/h',
    note: 'Based on the Energy Star room air-conditioner chart. Sizing for whole-house systems needs a Manual J load calculation.',
    fields: [
      num('area', 'Room area (sq ft)', '300'),
      select('sun', 'Sun exposure', '0', [
        ['Normal', '0'],
        ['Very sunny (+10%)', '1'],
        ['Heavily shaded (−10%)', '2'],
      ]),
      num('people', 'People usually in the room', '2'),
      select('kitchen', 'Is it a kitchen?', '0', [
        ['No', '0'],
        ['Yes (+4,000 BTU)', '1'],
      ]),
    ],
    compute: ({ area, sun, people, kitchen }) => {
      const row = BTU_CHART.find(([max]) => area <= max);
      let btu = row ? row[1] : Math.round(area * 14);
      if (sun === 1) btu *= 1.1;
      if (sun === 2) btu *= 0.9;
      if (people > 2) btu += 600 * (people - 2);
      if (kitchen === 1) btu += 4000;
      return [
        { label: 'Recommended cooling capacity', value: `${fmt(btu, 0)} BTU/h` },
        { label: 'In tons of cooling', value: fmt(btu / 12000, 2) },
        { label: 'In kilowatts', value: `${fmt(btu * 0.00029307107, 2)} kW` },
      ];
    },
  },
  'parallel-resistor-calculator': {
    key: 'parallel-resistor-calculator',
    title: 'Parallel Resistor Calculator',
    badge: 'Ω ∥',
    note: NOTE,
    fields: [
      num('r1', 'R1 (Ω)', '100'),
      num('r2', 'R2 (Ω)', '220'),
      num('r3', 'R3 (Ω, optional)', '0'),
      num('r4', 'R4 (Ω, optional)', '0'),
    ],
    compute: ({ r1, r2, r3, r4 }) => {
      const rs = [r1, r2, r3, r4].filter((r) => r > 0);
      if (rs.length < 2) return [{ label: 'Result', value: 'Enter at least two resistances' }];
      const par = 1 / rs.reduce((s, r) => s + 1 / r, 0);
      return [
        { label: 'Parallel resistance', value: `${fmt(par, 3)} Ω` },
        {
          label: 'Series resistance (for comparison)',
          value: `${fmt(
            rs.reduce((s, r) => s + r, 0),
            3,
          )} Ω`,
        },
      ];
    },
  },
  'amps-to-watts-calculator': {
    key: 'amps-to-watts-calculator',
    title: 'Amps to Watts Calculator',
    badge: 'A → W',
    note: NOTE,
    fields: [
      num('a', 'Current (A)', '10'),
      num('v', 'Voltage (V)', '120'),
      select('type', 'Circuit', '0', [
        ['DC', '0'],
        ['AC single-phase', '1'],
        ['AC three-phase (line voltage)', '2'],
      ]),
      num('pf', 'Power factor (AC)', '1'),
    ],
    compute: ({ a, v, type, pf }) => {
      const w = type === 0 ? a * v : type === 1 ? a * v * pf : Math.sqrt(3) * a * v * pf;
      return [
        { label: 'Power', value: `${fmt(w, 2)} W` },
        { label: 'In kilowatts', value: `${fmt(w / 1000, 3)} kW` },
        {
          label: 'Formula',
          value: type === 0 ? 'P = V × I' : type === 1 ? 'P = V × I × PF' : 'P = √3 × V × I × PF',
        },
      ];
    },
  },
  'sample-size-calculator': {
    key: 'sample-size-calculator',
    title: 'Sample Size Calculator',
    badge: 'n',
    note: NOTE,
    fields: [
      select('cl', 'Confidence level', '1.96', [
        ['90%', '1.645'],
        ['95%', '1.96'],
        ['99%', '2.576'],
      ]),
      num('e', 'Margin of error (%)', '5'),
      num('p', 'Expected proportion (%)', '50'),
      num('pop', 'Population size (0 = very large)', '0'),
    ],
    compute: ({ cl, e, p, pop }) => {
      const E = e / 100;
      const P = p / 100;
      if (E <= 0) return [{ label: 'Sample size', value: 'Enter a margin of error above 0' }];
      const n0 = (cl * cl * P * (1 - P)) / (E * E);
      const n = pop > 0 ? n0 / (1 + (n0 - 1) / pop) : n0;
      return [
        { label: 'Sample size needed', value: fmt(Math.ceil(n), 0) },
        { label: 'Before finite-population correction', value: fmt(Math.ceil(n0), 0) },
        { label: 'Formula', value: 'n₀ = z² p(1 − p) / e² (Cochran)' },
      ];
    },
  },
  'voltage-drop-calculator': {
    key: 'voltage-drop-calculator',
    title: 'Voltage Drop Calculator',
    badge: 'VD',
    note: 'Uses NEC Chapter 9 Table 8 DC resistance at 75°C; AC drop can be slightly higher. Check the NEC and your inspector.',
    fields: [
      select(
        'awg',
        'Wire size',
        '1',
        AWG.map(([n], i) => [n, String(i)]),
      ),
      select('mat', 'Conductor', '0', [
        ['Copper', '0'],
        ['Aluminum', '1'],
      ]),
      num('len', 'One-way length (ft)', '100'),
      num('amps', 'Load current (A)', '16'),
      num('v', 'Source voltage (V)', '120'),
      select('ph', 'Phase', '0', [
        ['Single-phase / DC', '0'],
        ['Three-phase', '1'],
      ]),
    ],
    compute: ({ awg, mat, len, amps, v, ph }) => {
      const row = AWG[awg] ?? AWG[1];
      const r = mat === 1 ? row[2] : row[1];
      const vd = ((ph === 1 ? Math.sqrt(3) : 2) * len * amps * r) / 1000;
      const pct = (vd / v) * 100;
      return [
        { label: 'Voltage drop', value: `${fmt(vd, 2)} V (${fmt(pct, 2)}%)` },
        { label: 'Voltage at the load', value: `${fmt(v - vd, 1)} V` },
        {
          label: 'NEC informational note (3% branch)',
          value: pct <= 3 ? 'Within 3%' : 'Over 3%: consider a larger wire',
        },
      ];
    },
  },
  '555-timer-calculator': {
    key: '555-timer-calculator',
    title: '555 Timer Calculator',
    badge: 'NE555',
    note: NOTE,
    fields: [num('r1', 'R1 (kΩ)', '1'), num('r2', 'R2 (kΩ)', '10'), num('c', 'C (µF)', '10')],
    compute: ({ r1, r2, c }) => {
      const k = 1e-3; // kΩ × µF = ms
      const high = 0.693 * (r1 + r2) * c * k;
      const low = 0.693 * r2 * c * k;
      const f = 1 / (high + low);
      return [
        { label: 'Frequency (astable)', value: `${fmt(f, 3)} Hz` },
        { label: 'Time high', value: `${fmt(high * 1000, 2)} ms` },
        { label: 'Time low', value: `${fmt(low * 1000, 2)} ms` },
        { label: 'Duty cycle', value: `${fmt(((r1 + r2) / (r1 + 2 * r2)) * 100, 1)}%` },
        { label: 'Monostable pulse with R1 and C', value: `${fmt(1.1 * r1 * c, 3)} ms` },
      ];
    },
  },
  'gear-ratio-calculator': {
    key: 'gear-ratio-calculator',
    title: 'Gear Ratio Calculator',
    badge: 'Ratio',
    note: NOTE,
    fields: [
      num('drive', 'Driving gear teeth', '12'),
      num('driven', 'Driven gear teeth', '36'),
      num('rpm', 'Input speed (RPM)', '1800'),
      num('torque', 'Input torque (N·m)', '10'),
    ],
    compute: ({ drive, driven, rpm, torque }) => {
      if (!drive || !driven) return [{ label: 'Ratio', value: 'Enter both tooth counts' }];
      const ratio = driven / drive;
      return [
        { label: 'Gear ratio', value: `${fmt(ratio, 3)}:1` },
        { label: 'Output speed', value: `${fmt(rpm / ratio, 1)} RPM` },
        { label: 'Output torque (ideal, no losses)', value: `${fmt(torque * ratio, 2)} N·m` },
        {
          label: 'Type',
          value:
            ratio > 1
              ? 'Reduction (slower, more torque)'
              : ratio < 1
                ? 'Overdrive (faster, less torque)'
                : '1:1',
        },
      ];
    },
  },
  'gravel-calculator': {
    key: 'gravel-calculator',
    title: 'Gravel Calculator',
    badge: 'yd³',
    note: BUILD,
    fields: areaFields,
    compute: ({ l, wd, d }) => {
      const yd = volumeYd(l, wd, d);
      return [
        { label: 'Gravel needed', value: `${fmt(yd, 2)} cubic yards` },
        { label: 'Approximate weight (1.4 t/yd³)', value: `${fmt(yd * 1.4, 2)} tons` },
        { label: 'In cubic feet', value: fmt(yd * 27, 1) },
        { label: '0.5 cu ft bags', value: fmt(Math.ceil((yd * 27) / 0.5), 0) },
      ];
    },
  },
  'mulch-calculator': {
    key: 'mulch-calculator',
    title: 'Mulch Calculator',
    badge: 'Mulch',
    note: BUILD,
    fields: [
      num('l', 'Bed length (ft)', '20'),
      num('wd', 'Bed width (ft)', '4'),
      num('d', 'Depth (inches)', '3'),
    ],
    compute: ({ l, wd, d }) => {
      const yd = volumeYd(l, wd, d);
      return [
        { label: 'Mulch needed', value: `${fmt(yd, 2)} cubic yards` },
        { label: '2 cu ft bags', value: fmt(Math.ceil((yd * 27) / 2), 0) },
        { label: '3 cu ft bags', value: fmt(Math.ceil((yd * 27) / 3), 0) },
        { label: 'Area covered', value: `${fmt(l * wd, 0)} sq ft` },
      ];
    },
  },
  'soil-calculator': {
    key: 'soil-calculator',
    title: 'Soil Calculator',
    badge: 'Soil',
    note: BUILD,
    fields: [
      num('l', 'Length (ft)', '8'),
      num('wd', 'Width (ft)', '4'),
      num('d', 'Depth (inches)', '12'),
    ],
    compute: ({ l, wd, d }) => {
      const yd = volumeYd(l, wd, d);
      return [
        { label: 'Soil needed', value: `${fmt(yd, 2)} cubic yards` },
        { label: 'In cubic feet', value: fmt(yd * 27, 1) },
        { label: '1 cu ft bags', value: fmt(Math.ceil(yd * 27), 0) },
        { label: 'In litres', value: fmt(yd * 764.554858, 0) },
      ];
    },
  },
  'sand-calculator': {
    key: 'sand-calculator',
    title: 'Sand Calculator',
    badge: 'Sand',
    note: BUILD,
    fields: areaFields,
    compute: ({ l, wd, d }) => {
      const yd = volumeYd(l, wd, d);
      return [
        { label: 'Sand needed', value: `${fmt(yd, 2)} cubic yards` },
        { label: 'Approximate weight (1.35 t/yd³, dry)', value: `${fmt(yd * 1.35, 2)} tons` },
        { label: '50 lb bags (about 0.5 cu ft each)', value: fmt(Math.ceil((yd * 27) / 0.5), 0) },
      ];
    },
  },
  'cubic-yard-calculator': {
    key: 'cubic-yard-calculator',
    title: 'Cubic Yard Calculator',
    badge: 'yd³',
    note: BUILD,
    fields: areaFields,
    compute: ({ l, wd, d }) => {
      const yd = volumeYd(l, wd, d);
      return [
        { label: 'Volume', value: `${fmt(yd, 3)} cubic yards` },
        { label: 'In cubic feet', value: fmt(yd * 27, 2) },
        { label: 'In cubic meters', value: fmt(yd * 0.764554858, 3) },
      ];
    },
  },
  'stair-calculator': {
    key: 'stair-calculator',
    title: 'Stair Calculator',
    badge: 'Stairs',
    note: 'Checks against IRC R311.7.5 (max riser 7¾", min tread 10"). Local codes can differ.',
    fields: [
      num('rise', 'Total rise, floor to floor (inches)', '108'),
      num('maxR', 'Maximum riser height (inches)', '7.75'),
      num('tread', 'Tread depth (inches)', '10.5'),
    ],
    compute: ({ rise, maxR, tread }) => {
      if (rise <= 0 || maxR <= 0) return [{ label: 'Stairs', value: 'Enter the total rise' }];
      const risers = Math.ceil(rise / maxR);
      const rh = rise / risers;
      const treads = risers - 1;
      const run = treads * tread;
      return [
        { label: 'Number of risers', value: String(risers) },
        { label: 'Riser height', value: `${fmt(rh, 3)} in` },
        { label: 'Number of treads', value: String(treads) },
        { label: 'Total run', value: `${fmt(run, 2)} in (${fmt(run / 12, 2)} ft)` },
        { label: 'Stringer length', value: `${fmt(Math.hypot(rise, run), 2)} in` },
        { label: 'Stair angle', value: `${fmt((Math.atan(rh / tread) * 180) / Math.PI, 1)}°` },
        {
          label: 'IRC check',
          value:
            rh <= 7.75 && tread >= 10
              ? 'Meets the IRC riser and tread limits'
              : 'Outside IRC limits',
        },
      ];
    },
  },
  'roof-pitch-calculator': {
    key: 'roof-pitch-calculator',
    title: 'Roof Pitch Calculator',
    badge: 'x/12',
    note: NOTE,
    fields: [num('rise', 'Rise (inches)', '6'), num('run', 'Run (inches)', '12')],
    compute: ({ rise, run }) => {
      if (!run) return [{ label: 'Pitch', value: 'Run cannot be 0' }];
      const p = (rise / run) * 12;
      return [
        { label: 'Pitch', value: `${fmt(p, 2)}/12` },
        { label: 'Angle', value: `${fmt((Math.atan(rise / run) * 180) / Math.PI, 2)}°` },
        { label: 'Slope', value: `${fmt((rise / run) * 100, 1)}%` },
        { label: 'Rafter / area multiplier', value: fmt(Math.sqrt(1 + (rise / run) ** 2), 4) },
      ];
    },
  },
  'roofing-calculator': {
    key: 'roofing-calculator',
    title: 'Roofing Calculator',
    badge: 'Squares',
    note: BUILD,
    fields: [
      num('area', 'House footprint incl. overhangs (sq ft)', '1800'),
      num('pitch', 'Roof pitch (inches per 12)', '6'),
      num('waste', 'Waste (%)', '10'),
    ],
    compute: ({ area, pitch, waste }) => {
      const mult = Math.sqrt(1 + (pitch / 12) ** 2);
      const roof = area * mult;
      const squares = (roof * (1 + waste / 100)) / 100;
      return [
        { label: 'Roof area', value: `${fmt(roof, 0)} sq ft` },
        { label: 'Roofing squares (with waste)', value: fmt(squares, 2) },
        { label: 'Shingle bundles (3 per square)', value: fmt(Math.ceil(squares * 3), 0) },
        { label: 'Pitch multiplier', value: fmt(mult, 4) },
      ];
    },
  },
  'brick-calculator': {
    key: 'brick-calculator',
    title: 'Brick Calculator',
    badge: 'Bricks',
    note: BUILD,
    fields: [
      num('l', 'Wall length (ft)', '20'),
      num('h', 'Wall height (ft)', '8'),
      num('open', 'Openings to subtract (sq ft)', '0'),
      select('size', 'Brick', '6.857', [
        ['Modular 7⅝ × 2¼ in (6.86 per sq ft)', '6.857'],
        ['Standard 8 × 2¼ in (6.55 per sq ft)', '6.55'],
        ['Queen 7⅝ × 2¾ in (5.76 per sq ft)', '5.76'],
        ['Utility 11⅝ × 3⅝ in (3.0 per sq ft)', '3'],
      ]),
      num('waste', 'Waste (%)', '5'),
    ],
    compute: ({ l, h, open, size, waste }) => {
      const area = Math.max(0, l * h - open);
      const bricks = Math.ceil(area * size * (1 + waste / 100));
      return [
        { label: 'Bricks needed', value: fmt(bricks, 0) },
        { label: 'Wall area', value: `${fmt(area, 1)} sq ft` },
        {
          label: 'Mortar (about 7 bags per 1,000 bricks)',
          value: `${fmt(Math.ceil((bricks / 1000) * 7), 0)} bags`,
        },
      ];
    },
  },
  'concrete-block-calculator': {
    key: 'concrete-block-calculator',
    title: 'Concrete Block Calculator',
    badge: 'CMU',
    note: BUILD,
    fields: [
      num('l', 'Wall length (ft)', '40'),
      num('h', 'Wall height (ft)', '4'),
      num('open', 'Openings (sq ft)', '0'),
      num('waste', 'Waste (%)', '5'),
    ],
    compute: ({ l, h, open, waste }) => {
      const area = Math.max(0, l * h - open);
      const blocks = Math.ceil(area * 1.125 * (1 + waste / 100));
      return [
        { label: '8 × 16 in blocks needed', value: fmt(blocks, 0) },
        { label: 'Courses (8 in high)', value: fmt(Math.ceil((h * 12) / 8), 0) },
        { label: 'Wall area', value: `${fmt(area, 1)} sq ft` },
      ];
    },
  },
  'board-foot-calculator': {
    key: 'board-foot-calculator',
    title: 'Board Foot Calculator',
    badge: 'BF',
    note: NOTE,
    fields: [
      num('t', 'Thickness (inches)', '1'),
      num('w', 'Width (inches)', '6'),
      num('l', 'Length (feet)', '8'),
      num('q', 'Pieces', '10'),
      num('price', 'Price per board foot (optional)', '0'),
    ],
    compute: ({ t, w, l, q, price }) => {
      const bf = ((t * w * l) / 12) * Math.max(1, q);
      return [
        { label: 'Board feet', value: fmt(bf, 2) },
        { label: 'Per piece', value: fmt((t * w * l) / 12, 3) },
        ...(price > 0 ? [{ label: 'Cost', value: fmt(bf * price, 2) }] : []),
      ];
    },
  },
  'decking-calculator': {
    key: 'decking-calculator',
    title: 'Decking Calculator',
    badge: 'Deck',
    note: BUILD,
    fields: [
      num('dl', 'Deck length, along the boards (ft)', '16'),
      num('dw', 'Deck width, across the boards (ft)', '12'),
      num('bw', 'Board width (inches, 5.5 for a 6" board)', '5.5'),
      num('gap', 'Gap between boards (inches)', '0.125'),
      num('bl', 'Board length (ft)', '16'),
    ],
    compute: ({ dl, dw, bw, gap, bl }) => {
      const rows = Math.ceil((dw * 12) / (bw + gap));
      const perRow = Math.ceil(dl / bl);
      return [
        { label: 'Boards needed', value: fmt(rows * perRow, 0) },
        { label: 'Rows of boards', value: fmt(rows, 0) },
        { label: 'Linear feet', value: fmt(rows * dl, 0) },
        { label: 'Deck area', value: `${fmt(dl * dw, 0)} sq ft` },
      ];
    },
  },
  'fence-calculator': {
    key: 'fence-calculator',
    title: 'Fence Calculator',
    badge: 'Fence',
    note: BUILD,
    fields: [
      num('len', 'Fence length (ft)', '100'),
      num('sp', 'Post spacing (ft)', '8'),
      num('rails', 'Rails per section', '3'),
      num('pw', 'Picket width (inches)', '5.5'),
      num('pg', 'Gap between pickets (inches)', '0.5'),
    ],
    compute: ({ len, sp, rails, pw, pg }) => {
      const sections = Math.ceil(len / sp);
      return [
        { label: 'Posts', value: fmt(sections + 1, 0) },
        { label: 'Sections', value: fmt(sections, 0) },
        { label: 'Rails', value: fmt(sections * rails, 0) },
        { label: 'Pickets', value: fmt(Math.ceil((len * 12) / (pw + pg)), 0) },
      ];
    },
  },
  'wallpaper-calculator': {
    key: 'wallpaper-calculator',
    title: 'Wallpaper Calculator',
    badge: 'Rolls',
    note: BUILD,
    fields: [
      num('per', 'Total wall width (perimeter, ft)', '48'),
      num('h', 'Wall height (ft)', '8'),
      num('open', 'Doors and windows (sq ft)', '40'),
      select('roll', 'Roll', '56', [
        ['US double roll (about 56 sq ft)', '56'],
        ['European roll 0.53 × 10.05 m (57 sq ft)', '57.3'],
        ['US single roll (about 28 sq ft)', '28'],
      ]),
    ],
    compute: ({ per, h, open, roll }) => {
      const area = Math.max(0, per * h - open);
      // 15% allowance for pattern matching and trimming.
      const rolls = Math.ceil((area * 1.15) / roll);
      return [
        { label: 'Rolls needed', value: fmt(rolls, 0) },
        {
          label: 'Wall area to cover',
          value: `${fmt(area, 0)} sq ft (${fmt(area * 0.092903, 1)} m²)`,
        },
        { label: 'Includes', value: '15% for pattern matching and trimming' },
      ];
    },
  },
  'cbm-calculator': {
    key: 'cbm-calculator',
    title: 'CBM Calculator',
    badge: 'm³',
    note: NOTE,
    fields: [
      num('l', 'Length (cm)', '60'),
      num('w', 'Width (cm)', '40'),
      num('h', 'Height (cm)', '40'),
      num('q', 'Number of cartons', '50'),
      num('kg', 'Gross weight per carton (kg, optional)', '12'),
    ],
    compute: ({ l, w, h, q, kg }) => {
      const cbm = ((l * w * h) / 1e6) * Math.max(1, q);
      const total = kg * Math.max(1, q);
      return [
        { label: 'Total volume', value: `${fmt(cbm, 3)} CBM (m³)` },
        { label: 'In cubic feet', value: fmt(cbm * 35.3146667, 2) },
        {
          label: 'Share of a 20 ft container (about 33 m³)',
          value: `${fmt((cbm / 33) * 100, 1)}%`,
        },
        ...(total > 0
          ? [
              {
                label: 'Sea freight chargeable (1 CBM = 1,000 kg)',
                value: `${fmt(Math.max(cbm, total / 1000), 3)} W/M`,
              },
            ]
          : []),
      ];
    },
  },
  'pool-salt-calculator': {
    key: 'pool-salt-calculator',
    title: 'Pool Salt Calculator',
    badge: 'ppm',
    note: 'Check your chlorinator’s manual for its target range; most ask for 2,700-3,400 ppm.',
    fields: [
      num('gal', 'Pool volume (US gallons)', '20000'),
      num('cur', 'Current salt level (ppm)', '1500'),
      num('tgt', 'Target salt level (ppm)', '3200'),
    ],
    compute: ({ gal, cur, tgt }) => {
      const lb = (gal * Math.max(0, tgt - cur) * 8.34) / 1e6;
      return [
        { label: 'Salt to add', value: `${fmt(lb, 0)} lb (${fmt(lb * 0.4536, 0)} kg)` },
        { label: '40 lb bags', value: fmt(Math.ceil(lb / 40), 0) },
        ...(cur > tgt
          ? [
              {
                label: 'Note',
                value: 'Salt is already above target; dilute with fresh water to lower it',
              },
            ]
          : []),
      ];
    },
  },
  'metal-weight-calculator': {
    key: 'metal-weight-calculator',
    title: 'Metal Weight Calculator',
    badge: 'kg',
    note: NOTE,
    fields: [
      select('shape', 'Shape', '0', [
        ['Plate / sheet (a × b × thickness c)', '0'],
        ['Round bar (diameter a, length b)', '1'],
        ['Square bar (side a, length b)', '2'],
        ['Round tube (OD a, wall c, length b)', '3'],
      ]),
      num('a', 'a (mm)', '1000'),
      num('b', 'b (mm)', '2000'),
      num('c', 'c (mm)', '5'),
      select('rho', 'Material', '7850', [
        ['Steel (7,850 kg/m³)', '7850'],
        ['Stainless steel (8,000)', '8000'],
        ['Aluminum (2,700)', '2700'],
        ['Copper (8,960)', '8960'],
        ['Brass (8,500)', '8500'],
        ['Titanium (4,500)', '4500'],
      ]),
      num('q', 'Pieces', '1'),
    ],
    compute: ({ shape, a, b, c, rho, q }) => {
      const mm3 =
        shape === 0
          ? a * b * c
          : shape === 1
            ? Math.PI * (a / 2) ** 2 * b
            : shape === 2
              ? a * a * b
              : Math.PI * ((a / 2) ** 2 - (a / 2 - c) ** 2) * b;
      const kg = mm3 * 1e-9 * rho * Math.max(1, q);
      return [
        { label: 'Weight', value: `${fmt(kg, 3)} kg (${fmt(kg * 2.20462, 2)} lb)` },
        { label: 'Volume', value: `${fmt(mm3 / 1e6, 3)} L per piece` },
      ];
    },
  },
  'sphere-volume-calculator': {
    key: 'sphere-volume-calculator',
    title: 'Sphere Volume Calculator',
    badge: '⁴⁄₃πr³',
    note: NOTE,
    fields: [
      select('known', 'I know the', '0', [
        ['Radius', '0'],
        ['Diameter', '1'],
      ]),
      num('v', 'Value', '5'),
    ],
    compute: ({ known, v }) => {
      const r = known === 1 ? v / 2 : v;
      return [
        { label: 'Volume', value: fmt((4 / 3) * Math.PI * r ** 3, 4) },
        { label: 'Surface area', value: fmt(4 * Math.PI * r * r, 4) },
        { label: 'Radius', value: fmt(r, 4) },
      ];
    },
  },
  'combination-calculator': {
    key: 'combination-calculator',
    title: 'Combination Calculator',
    badge: 'nCr',
    note: NOTE,
    fields: [num('n', 'Total items n', '10'), num('r', 'Chosen r', '3')],
    compute: ({ n, r }) => {
      const N = Math.round(n);
      const R = Math.round(r);
      if (N < 0 || R < 0 || R > N || N > 1000)
        return [{ label: 'Result', value: 'Use whole numbers with 0 ≤ r ≤ n ≤ 1000' }];
      let perm = BigInt(1);
      for (let i = 0; i < R; i += 1) perm *= BigInt(N - i);
      let fact = BigInt(1);
      for (let i = 2; i <= R; i += 1) fact *= BigInt(i);
      const comb = perm / fact;
      const show = (x: bigint) =>
        x.toString().length > 30
          ? `${x.toString().slice(0, 6)}… (${x.toString().length} digits)`
          : BigInt(x).toLocaleString('en-US');
      return [
        { label: `Combinations C(${N}, ${R}), order does not matter`, value: show(comb) },
        { label: `Permutations P(${N}, ${R}), order matters`, value: show(perm) },
      ];
    },
  },
  'antilog-calculator': {
    key: 'antilog-calculator',
    title: 'Antilog Calculator',
    badge: 'bˣ',
    note: NOTE,
    fields: [
      num('x', 'Logarithm value x', '2.5'),
      select('base', 'Base', '10', [
        ['10 (common)', '10'],
        ['e (natural)', '2.718281828459045'],
        ['2 (binary)', '2'],
      ]),
    ],
    compute: ({ x, base }) => [
      { label: 'Antilog', value: fmt(base ** x, 8) },
      {
        label: 'Check',
        value: `log of the result in this base = ${fmt(Math.log(base ** x) / Math.log(base), 6)}`,
      },
    ],
  },
  'marks-percentage-calculator': {
    key: 'marks-percentage-calculator',
    title: 'Marks Percentage Calculator',
    badge: '%',
    note: NOTE,
    fields: [num('got', 'Marks obtained', '432'), num('total', 'Total marks', '500')],
    compute: ({ got, total }) => {
      if (!total) return [{ label: 'Percentage', value: 'Enter the total marks' }];
      const p = (got / total) * 100;
      return [
        { label: 'Percentage', value: `${fmt(p, 2)}%` },
        { label: 'Marks lost', value: fmt(total - got, 1) },
        { label: 'CGPA equivalent (CBSE, % ÷ 9.5)', value: fmt(p / 9.5, 2) },
      ];
    },
  },
  'download-time-calculator': {
    key: 'download-time-calculator',
    title: 'Download Time Calculator',
    badge: 'Mbps',
    note: NOTE,
    fields: [
      num('size', 'File size', '5'),
      select('su', 'Size unit', '1000000000', [
        ['MB', '1000000'],
        ['GB', '1000000000'],
        ['TB', '1000000000000'],
      ]),
      num('speed', 'Connection speed (Mbps)', '100'),
    ],
    compute: ({ size, su, speed }) => {
      if (!speed) return [{ label: 'Time', value: 'Enter a speed above 0' }];
      const s = (size * su * 8) / (speed * 1e6);
      return [
        { label: 'Download time (ideal)', value: duration(s) },
        { label: 'At a realistic 80% of the line speed', value: duration(s / 0.8) },
        { label: 'Speed in MB/s', value: fmt(speed / 8, 2) },
      ];
    },
  },
  'time-value-of-money-calculator': {
    key: 'time-value-of-money-calculator',
    title: 'Time Value of Money Calculator',
    badge: 'TVM',
    note: 'Sign convention as on a financial calculator: money you pay out is negative, money you receive is positive.',
    fields: [
      select('solve', 'Solve for', '4', [
        ['Future value (FV)', '4'],
        ['Present value (PV)', '2'],
        ['Payment (PMT)', '3'],
        ['Number of periods (N)', '0'],
        ['Interest rate (I/Y)', '1'],
      ]),
      num('n', 'Number of periods (N)', '120'),
      num('rate', 'Annual interest rate I/Y (%)', '6'),
      num('pv', 'Present value (PV)', '-10000'),
      num('pmt', 'Payment per period (PMT)', '-200'),
      num('fv', 'Future value (FV)', '0'),
      select('py', 'Periods per year', '12', [
        ['Monthly (12)', '12'],
        ['Quarterly (4)', '4'],
        ['Yearly (1)', '1'],
        ['Weekly (52)', '52'],
        ['Daily (365)', '365'],
      ]),
      select('when', 'Payments at', '0', [
        ['End of each period', '0'],
        ['Start of each period', '1'],
      ]),
    ],
    compute: ({ solve, n, rate, pv, pmt, fv, py, when }) => {
      // PV·(1+i)^n + PMT·(1+i·t)·((1+i)^n − 1)/i + FV = 0
      const bal = (i: number, N: number, PV: number, PMT: number, FV: number) =>
        i === 0
          ? PV + PMT * N + FV
          : PV * (1 + i) ** N + PMT * (1 + i * when) * (((1 + i) ** N - 1) / i) + FV;
      const i = rate / 100 / py;
      let out: number;
      let label: string;
      if (solve === 4) {
        out = -bal(i, n, pv, pmt, 0);
        label = 'Future value (FV)';
      } else if (solve === 2) {
        const g = (1 + i) ** n;
        out = i === 0 ? -(pmt * n + fv) : -(pmt * (1 + i * when) * ((g - 1) / i) + fv) / g;
        label = 'Present value (PV)';
      } else if (solve === 3) {
        const g = (1 + i) ** n;
        out = i === 0 ? -(pv + fv) / n : -(pv * g + fv) / ((1 + i * when) * ((g - 1) / i));
        label = 'Payment per period (PMT)';
      } else if (solve === 0) {
        // Solve N by bisection on the balance; it is monotonic in N for a sensible cash flow.
        let lo = 0;
        let hi = 10000;
        const f0 = bal(i, lo, pv, pmt, fv);
        if (f0 * bal(i, hi, pv, pmt, fv) > 0)
          return [
            { label: 'Number of periods', value: 'No solution: check the signs of PV, PMT and FV' },
          ];
        for (let k = 0; k < 200; k += 1) {
          const mid = (lo + hi) / 2;
          if (f0 * bal(i, mid, pv, pmt, fv) <= 0) hi = mid;
          else lo = mid;
        }
        out = (lo + hi) / 2;
        return [
          { label: 'Number of periods (N)', value: fmt(out, 2) },
          { label: 'In years', value: fmt(out / py, 2) },
        ];
      } else {
        // Rate: bisection between −99% and +100% per period.
        let lo = -0.99;
        let hi = 1;
        const f = (r: number) => bal(r, n, pv, pmt, fv);
        if (f(lo) * f(hi) > 0)
          return [
            { label: 'Interest rate', value: 'No solution: check the signs of PV, PMT and FV' },
          ];
        for (let k = 0; k < 200; k += 1) {
          const mid = (lo + hi) / 2;
          if (f(lo) * f(mid) <= 0) hi = mid;
          else lo = mid;
        }
        const per = (lo + hi) / 2;
        return [
          { label: 'Annual rate (I/Y, nominal)', value: `${fmt(per * py * 100, 4)}%` },
          { label: 'Rate per period', value: `${fmt(per * 100, 5)}%` },
          { label: 'Effective annual rate', value: `${fmt(((1 + per) ** py - 1) * 100, 4)}%` },
        ];
      }
      const totalPaid = pmt * n;
      return [
        { label, value: fmt(out, 2) },
        { label: 'Total of payments (PMT × N)', value: fmt(solve === 3 ? out * n : totalPaid, 2) },
        { label: 'Effective annual rate', value: `${fmt(((1 + i) ** py - 1) * 100, 4)}%` },
      ];
    },
  },
  'holiday-entitlement-calculator': {
    key: 'holiday-entitlement-calculator',
    title: 'Holiday Entitlement Calculator',
    badge: 'UK',
    note: 'UK statutory minimum (Working Time Regulations 1998 and the 2024 irregular-hours rules). Your contract may give more. Check GOV.UK for your situation.',
    fields: [
      select('basis', 'Work pattern', '0', [
        ['Fixed days per week', '0'],
        ['Fixed hours per week', '1'],
        ['Irregular hours or part-year (hours worked)', '2'],
      ]),
      num('days', 'Days worked per week', '5'),
      num('hours', 'Hours worked per week', '37.5'),
      num('worked', 'Hours worked in the pay period (irregular)', '160'),
      num('months', 'Months of the leave year worked (12 = full year)', '12'),
    ],
    compute: ({ basis, days, hours, worked, months }) => {
      const frac = Math.min(12, Math.max(0, months)) / 12;
      if (basis === 2) {
        // From 1 April 2024: 12.07% of hours worked in each pay period.
        const h = worked * 0.1207;
        return [
          { label: 'Holiday accrued this pay period', value: `${fmt(h, 2)} hours` },
          { label: 'Rule', value: '12.07% of hours worked (5.6 weeks ÷ 46.4 working weeks)' },
        ];
      }
      if (basis === 1) {
        const h = 5.6 * hours * frac;
        return [
          { label: 'Statutory holiday', value: `${fmt(h, 2)} hours` },
          { label: 'Full-year entitlement', value: `${fmt(5.6 * hours, 2)} hours` },
          { label: 'In weeks', value: '5.6 weeks of your normal working hours (28 days at most)' },
        ];
      }
      const d = Math.min(7, Math.max(0, days));
      const fullYear = Math.min(28, 5.6 * d);
      const ent = fullYear * frac;
      return [
        { label: 'Statutory holiday', value: `${fmt(ent, 2)} days` },
        { label: 'Full-year entitlement', value: `${fmt(fullYear, 2)} days` },
        {
          label: 'Rounded up to the next half day (optional)',
          value: `${fmt(Math.ceil(ent * 2) / 2, 1)} days`,
        },
        {
          label: 'Formula',
          value: `5.6 × ${fmt(d, 2)} days${d * 5.6 > 28 ? ', capped at 28' : ''}${frac < 1 ? ` × ${fmt(frac * 12, 2)}/12 of the year` : ''}`,
        },
      ];
    },
  },
};
